import { useState, useEffect, useCallback } from 'react';
import adminApi from '../../services/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner';
import { AdminModal, AdminField, AdminCheck, ConfirmModal } from '../../components/AdminModal';
import { apiErrorMessage } from '../../utils/apiError';
import {
  IoAddOutline,
  IoCreateOutline,
  IoTrashOutline,
  IoRefreshOutline
} from 'react-icons/io5';
import AdminDataTable, { AdminPill } from '../../components/admin/AdminDataTable';
import BulkActionHost from '../../components/admin/BulkActionHost';
import { useBulkAction, useRowSelection, runBulk, summarizeBulk } from '../../components/admin/useAdminTable';
import './AdminGoalsPage.css';

const BLANK = { key: '', name: '', description: '', targetValue: '', unit: '', isActive: true, displayOrder: 0 };

const GoalFormModal = ({ goal, onClose, onSaved }) => {
  const [form, setForm] = useState(goal ? {
    key: goal.key,
    name: goal.name,
    description: goal.description || '',
    targetValue: String(goal.targetValue),
    unit: goal.unit || '',
    isActive: goal.isActive,
    displayOrder: goal.displayOrder
  } : BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError(null);
      const { key, ...editable } = form;
      const payload = {
        ...(goal ? editable : { key, ...editable }),
        targetValue: parseInt(form.targetValue, 10),
        displayOrder: parseInt(form.displayOrder, 10) || 0
      };
      if (goal) {
        await adminApi.updateGoal(goal.id, payload);
      } else {
        await adminApi.createGoal(payload);
      }
      onSaved(`${goal ? 'Updated' : 'Created'} goal "${form.name}"`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save this goal.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModal
      title={goal ? `Edit ${goal.name}` : 'New site goal'}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="goal-form" className="admin-modal-btn is-primary" disabled={saving}>
            {saving ? 'Saving…' : goal ? 'Update goal' : 'Create goal'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      <form id="goal-form" onSubmit={handleSubmit}>
        {goal ? (
          // Read-only once created: the stats job writes against the key, so
          // renaming it silently stopped the value updating.
          <AdminField label="Key" hint="Fixed once created. Make a new goal to use a different key.">
            <input type="text" value={form.key} readOnly aria-readonly="true" />
          </AdminField>
        ) : (
          <AdminField label="Key" hint="The identifier the stats job writes against. total_chapters_read, total_readers, and total_bible_completions update automatically.">
            <input type="text" value={form.key} onChange={set('key')} required />
          </AdminField>
        )}
        <AdminField label="Name">
          <input type="text" value={form.name} onChange={set('name')} required />
        </AdminField>
        <AdminField label="Description">
          <textarea value={form.description} onChange={set('description')} rows={2} />
        </AdminField>
        <AdminField label="Target value">
          <input type="number" min="1" value={form.targetValue} onChange={set('targetValue')} required />
        </AdminField>
        <AdminField label="Unit" hint="For example: chapters, readers">
          <input type="text" value={form.unit} onChange={set('unit')} />
        </AdminField>
        <AdminField label="Display order">
          <input type="number" min="0" value={form.displayOrder} onChange={set('displayOrder')} />
        </AdminField>
        <AdminCheck label="Active" checked={form.isActive} onChange={set('isActive')} />
      </form>
    </AdminModal>
  );
};

const AdminGoalsPage = () => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  // Separate so refreshing values does not disable the delete dialog, and back
  const [refreshing, setRefreshing] = useState(false);
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.getGoals();
      setGoals(response.data.goals || []);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load site goals.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeModals = () => {
    setEditing(null);
    setCreating(false);
    setDeleting(null);
    setDeleteError(null);
  };

  const handleSaved = (message) => {
    closeModals();
    setNotice(message);
    load();
  };

  const handleDelete = async () => {
    try {
      setDeletingBusy(true);
      setDeleteError(null);
      await adminApi.deleteGoal(deleting.id);
      handleSaved(`Deleted goal "${deleting.name}"`);
    } catch (err) {
      setDeleteError(apiErrorMessage(err, 'Could not delete this goal.'));
    } finally {
      setDeletingBusy(false);
    }
  };

  const selection = useRowSelection(goals.map((goal) => goal.id).join(','));
  const bulk = useBulkAction((_result, text) => {
    selection.clear();
    setNotice(text);
    load();
  });

  const handleUpdateValues = async () => {
    try {
      setRefreshing(true);
      setError(null);
      await adminApi.updateGoalValues();
      setNotice('Recomputed the automatic goals from live site stats');
      load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not refresh goal values.'));
    } finally {
      setRefreshing(false);
    }
  };

  if (loading && goals.length === 0) {
    return (
      <div className="admin-loading">
        <LoadingSpinner size="large" message="Loading site goals..." />
      </div>
    );
  }

  return (
    <div className="admin-goals-page">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Site Goals</h1>
          <p className="admin-page-description">
            Targets shown to everyone on the site. Chapters read, readers, and Bible completions
            update from live stats; other goals keep the value you set.
          </p>
        </div>
        <div className="goal-header-actions">
          <button className="goal-primary-btn" onClick={() => setCreating(true)}>
            <IoAddOutline /> New Goal
          </button>
          <button className="goal-secondary-btn" onClick={handleUpdateValues} disabled={refreshing}>
            <IoRefreshOutline /> {refreshing ? 'Refreshing…' : 'Refresh Values'}
          </button>
        </div>
      </div>

      {notice && (
        <div className="goal-success-banner">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)}>&times;</button>
        </div>
      )}

      <AdminDataTable
        title={`Site goals (${goals.length})`}
        rows={goals}
        getRowLabel={(goal) => goal.name}
        selection={selection}
        loading={loading}
        error={error}
        onRetry={load}
        emptyTitle="No goals yet"
        emptyMessage="Create one to show a site-wide target."
        bulkActions={[
          {
            label: 'Deactivate',
            onClick: (rows) => {
              const wanted = rows.filter((row) => row.isActive !== false);
              if (wanted.length === 0) {
                setNotice('None of the selected goals are active.');
                return;
              }
              bulk.ask({
                title: 'Deactivate selected goals',
                confirmLabel: 'Deactivate',
                body: <p className="admin-modal-hint">Inactive goals stay stored but drop off the public site. This applies to {wanted.length} goal{wanted.length === 1 ? '' : 's'}.</p>,
                run: () => runBulk(wanted, (row) => adminApi.updateGoal(row.id, { isActive: false })),
                summarize: (result) => summarizeBulk('Deactivated', 'goal', result)
              });
            }
          },
          {
            label: 'Reactivate',
            danger: false,
            onClick: (rows) => {
              const wanted = rows.filter((row) => row.isActive === false);
              if (wanted.length === 0) {
                setNotice('None of the selected goals are inactive.');
                return;
              }
              bulk.ask({
                title: 'Reactivate selected goals',
                confirmLabel: 'Reactivate',
                danger: false,
                body: <p className="admin-modal-hint">These goals show on the public site again. This applies to {wanted.length} goal{wanted.length === 1 ? '' : 's'}.</p>,
                run: () => runBulk(wanted, (row) => adminApi.updateGoal(row.id, { isActive: true })),
                summarize: (result) => summarizeBulk('Reactivated', 'goal', result)
              });
            }
          },
          {
            label: 'Delete',
            danger: true,
            onClick: (rows) => bulk.ask({
              title: `Delete ${rows.length} goal${rows.length === 1 ? '' : 's'}`,
              confirmLabel: 'Delete goals',
              body: <p className="admin-modal-hint">This removes the goals and their stored progress. Reading data is untouched.</p>,
              run: () => runBulk(rows, (row) => adminApi.deleteGoal(row.id)),
              summarize: (result) => summarizeBulk('Deleted', 'goal', result)
            })
          }
        ]}
        columns={[
          {
            key: 'name',
            header: 'Goal',
            render: (goal) => (
              <span className="admin-cell-strong">
                {goal.name}
                <span className="admin-cell-sub">{goal.key}</span>
              </span>
            )
          },
          {
            key: 'status',
            header: 'Status',
            render: (goal) => (
              <>
                {goal.isCompleted && <AdminPill tone="ok">Completed</AdminPill>}
                {goal.isActive === false && <AdminPill tone="danger">Inactive</AdminPill>}
                {goal.isActive !== false && !goal.isCompleted && <AdminPill>Active</AdminPill>}
              </>
            )
          },
          {
            key: 'progress',
            header: 'Progress',
            className: 'goal-table-progress',
            render: (goal) => (
              <div className="goal-progress">
                <div className="goal-progress-numbers">
                  <strong>{goal.currentValue?.toLocaleString() ?? 0}</strong>
                  <span> / {(goal.effectiveTargetValue ?? goal.targetValue)?.toLocaleString()}</span>
                  {goal.unit && <span> {goal.unit}</span>}
                </div>
                <div className="goal-progress-bar">
                  <div className="goal-progress-fill" style={{ width: `${Math.min(goal.progress ?? 0, 100)}%` }} />
                </div>
                {goal.effectiveTargetValue != null && goal.effectiveTargetValue !== goal.targetValue && (
                  <small className="admin-modal-hint">Measured against the season target, as on the public site.</small>
                )}
              </div>
            )
          }
        ]}
        renderActions={(goal) => (
          <>
            <button type="button" onClick={() => setEditing(goal)}><IoCreateOutline /> Edit</button>
            <button type="button" className="is-danger" onClick={() => setDeleting(goal)}><IoTrashOutline /> Delete</button>
          </>
        )}
      />
      <BulkActionHost bulk={bulk} />

      {creating && <GoalFormModal onClose={closeModals} onSaved={handleSaved} />}
      {editing && <GoalFormModal goal={editing} onClose={closeModals} onSaved={handleSaved} />}

      {deleting && (
        <ConfirmModal
          title={`Delete ${deleting.name}`}
          confirmLabel="Delete goal"
          busy={deletingBusy}
          error={deleteError}
          onCancel={closeModals}
          onConfirm={handleDelete}
        >
          <p className="admin-modal-hint">
            This removes the goal and its stored progress. Reading data is untouched.
          </p>
        </ConfirmModal>
      )}
    </div>
  );
};

export default AdminGoalsPage;
